import { Icon, SkeletonLoader, Tag } from "@pepperstone/pepper-ui-kit";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import type { SavedPaymentAccountCalculatedTransactions } from "@/api/cashier";
import { SelectableCard } from "@/components/selectable-card/SelectableCard";
import { useCurrencies } from "@/hooks/deposit/useCurrencies";
import { useFormatAmount } from "@/hooks/useFormatAmount";
import { BreakdownTransactionList } from "@/pages/withdrawals/summary/breakdown-transaction-list/BreakdownTransactionList";
import type { WithdrawalDataUpdateProps } from "@/pages/withdrawals/summary/types";
import { moneyToMajorUnits } from "@/utils/helpers";
import styles from "./BreakdownTransactionCard.module.css";
import { useBreakdownTransactionGroups } from "./useBreakdownTransactionGroups";

export type BreakdownTransactionCardProps = WithdrawalDataUpdateProps & {
  isLoading: boolean;
  breakdown: SavedPaymentAccountCalculatedTransactions;
  collapsed?: boolean;
  isBankTransfer?: boolean;
};

export const BreakdownTransactionCard = ({
  isLoading,
  breakdown,
  collapsed,
  isBankTransfer = false,
  ...props
}: BreakdownTransactionCardProps) => {
  const { t } = useTranslation();
  const { formatAmount } = useFormatAmount();
  const { currencies, isLoading: currenciesLoading } = useCurrencies();

  const { refundNodes, profitNodes } = useBreakdownTransactionGroups({
    breakdown,
    collapsed,
    ...props,
  });

  const showAlert =
    (breakdown.profits && breakdown.profits?.length > 1) ||
    (breakdown.refunds && breakdown.refunds?.length > 1);

  const tradingCurrencyCode = breakdown.money.code;
  const currency = currencies?.[tradingCurrencyCode];
  const processingCurrency = breakdown.money_in_processing_currency?.code
    ? currencies?.[breakdown.money_in_processing_currency.code]
    : undefined;

  const fee =
    breakdown.fee_type === "percentage"
      ? t("withdrawals.fee.percentage", { value: breakdown.fee_value })
      : breakdown.fee_type === "fixed"
        ? t("withdrawals.fee.fixed", {
            value: formatAmount(
              moneyToMajorUnits(breakdown.fee_value),
              processingCurrency ?? currency
            ),
          })
        : t("withdrawals.fee.no_fee");

  const loading = currenciesLoading || isLoading;

  return (
    <SelectableCard
      className={clsx(styles.paymentCard)}
      classNames={{
        texts: "ps-flex-none",
        tag: "ps-flex-1",
        endText: "ps-flex-none ps-text-right",
        children: clsx(showAlert && "ps-mt-0"),
      }}
      id={breakdown.id}
      title={breakdown.name}
      topText={breakdown.detail}
      icon={breakdown.logo_url}
      iconPadding="xxs"
      tag={
        isBankTransfer ? undefined : (
          <Tag bgColor="foreground-neutral" label={fee} />
        )
      }
      hideCheckbox
      hideChildSeparatorLine
    >
      <div className="ps-flex ps-flex-col ps-gap-4 ps-p-4">
        {loading ? (
          <>
            <SkeletonLoader width={120} height={5} variant="rectangular" />
            <SkeletonLoader width={120} height={5} variant="rectangular" />
          </>
        ) : (
          <BreakdownTransactionList
            title={t("withdrawals.summary.breakdown.withdrawal_amount")}
            total={breakdown.money_total ?? breakdown.money}
            totalTestId="breakdown-transaction-list-item-amount-total-withdrawal"
            feeDescription={
              isBankTransfer
                ? t(
                    "withdrawals.summary.breakdown_transactions_card.withdrawal_limitation_message_with_fee"
                  )
                : undefined
            }
          />
        )}
      </div>
      {showAlert && (
        <div className="ps-p-4">
          <div className={`${styles.alert}`}>
            <Icon icon="alert-circle" />
            <div>
              {t(
                "withdrawals.summary.breakdown_transactions_card.withdrawal_limitation_message"
              )}
            </div>
          </div>
        </div>
      )}
      <div
        className={clsx(
          styles.paymentCardContent,
          "ps-flex ps-flex-col ps-gap-4 ps-p-4"
        )}
      >
        {loading ? (
          <>
            <SkeletonLoader width={120} height={5} variant="rectangular" />
            <SkeletonLoader width={120} height={5} variant="rectangular" />
          </>
        ) : (
          <>
            {refundNodes}
            {profitNodes}
          </>
        )}
      </div>
    </SelectableCard>
  );
};
